import { walkTreeByLevel } from "@voltalis/frontend-utils";
import { computed, Ref, ref } from "vue";

import { ISiteSectionsVirtualizedTableNode } from "@/components/site/site-sections-virtualized-table/site-sections-virtualized-table.types";
import { SiteSectionId } from "@/data/common/ids";
import { SectionNodePlanning } from "@/domains/v4b/site-programming-range/site-programming-range.types";
import { getAllLeafDescendants } from "@/utils/site-section/site-section-utils";

type TSectionNode = ISiteSectionsVirtualizedTableNode<SectionNodePlanning>;

export function useSiteSectionsPlanningSelection(nodes: Ref<TSectionNode[]>) {
  const selectedSectionsIds = ref<Set<SiteSectionId>>(new Set());

  const hashmapLeafSections = computed<Map<SiteSectionId, TSectionNode>>(() => {
    const result = new Map<SiteSectionId, TSectionNode>();

    for (const section of walkTreeByLevel(nodes.value)) {
      if (section.children.length) continue;
      result.set(section.data.sectionId, section);
    }

    return result;
  });

  const selectAllState = computed<boolean | null>(() => {
    if (hashmapLeafSections.value.size === 0) return false;
    if (selectedSectionsIds.value.size === hashmapLeafSections.value.size) return true;
    else if (selectedSectionsIds.value.size > 0) return null; // indeterminate
    return false;
  });

  function unselectSectionId(sectionId: SiteSectionId): void {
    selectedSectionsIds.value.delete(sectionId);
  }

  function deepToggleLeafSectionSelection(node: TSectionNode): void {
    if (node.children.length) {
      node.children.forEach((child: TSectionNode) => {
        deepToggleLeafSectionSelection(child);
      });

      return;
    }

    const isSectionAlreadySelected = selectedSectionsIds.value.has(node.data.sectionId);

    if (isSectionAlreadySelected) {
      unselectSectionId(node.data.sectionId);
    } else {
      selectedSectionsIds.value.add(node.data.sectionId);
    }
  }

  function isSectionDeepLeafDescendantsSelected(section: TSectionNode): boolean | null {
    const allLeafDescendants = getAllLeafDescendants(section);

    const isAllLeafDescendantsSelected = allLeafDescendants.every((child) =>
      selectedSectionsIds.value.has(child.data.sectionId),
    );
    if (isAllLeafDescendantsSelected) return true;

    const isAllLeafDescendantsUnselected = allLeafDescendants.every(
      (child) => !selectedSectionsIds.value.has(child.data.sectionId),
    );
    if (isAllLeafDescendantsUnselected) return false;

    return null; // indeterminate
  }

  function isSectionSelected(section: TSectionNode): boolean | null {
    if (section.children.length) {
      return isSectionDeepLeafDescendantsSelected(section);
    }

    return selectedSectionsIds.value.has(section.data.sectionId);
  }

  function selectAllSections() {
    unselectAllSections();

    for (const sectionId of hashmapLeafSections.value.keys()) {
      selectedSectionsIds.value.add(sectionId);
    }
  }

  function unselectAllSections() {
    selectedSectionsIds.value.clear();
  }

  function toggleSelectAllSections() {
    if (selectAllState.value === true) {
      unselectAllSections();
      return;
    }

    selectAllSections();
  }

  return {
    selectedSectionsIds,
    deepToggleLeafSectionSelection,
    isSectionSelected,
    selectAllState,
    toggleSelectAllSections,
    unselectAllSections,
  };
}
